import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { SvgTrophy, SvgStarFull } from '../../icons/v1';
import { SvgCheckCircle1 } from '../../icons/v2';
import { Text } from '../common/Text';
import { View } from '../common/View';

interface ProgressCelebrationProps {
  isVisible: boolean;
  onComplete: () => void;
  taskTitle?: string;
  isAllCompleted?: boolean;
}

export function ProgressCelebration({ 
  isVisible, 
  onComplete, 
  taskTitle, 
  isAllCompleted = false 
}: ProgressCelebrationProps) {
  const { isNarrowWidth } = useResponsive();
  const [showAnimation, setShowAnimation] = useState(false);

  const handleComplete = useCallback(() => {
    setShowAnimation(false);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    if (isVisible) {
      setShowAnimation(true);
      const timer = setTimeout(handleComplete, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, handleComplete]);

  if (!isVisible) {
    return null;
  }

  return (
    <View
      className="celebration-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    >
      <View
        style={{
          backgroundColor: theme.modalBackground,
          borderRadius: '20px',
          padding: isNarrowWidth ? '30px 20px' : '40px 30px',
          textAlign: 'center',
          maxWidth: isNarrowWidth ? '90vw' : '400px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          transform: showAnimation ? 'scale(1)' : 'scale(0.8)',
          opacity: showAnimation ? 1 : 0,
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Celebration Icon */}
        <View
          className="celebration-icon"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            animation: showAnimation ? 'bounce 1s ease-in-out' : 'none',
          }}
        >
          {isAllCompleted ? (
            <SvgTrophy
              style={{
                width: isNarrowWidth ? '60px' : '80px',
                height: isNarrowWidth ? '60px' : '80px',
                color: '#FFD700',
                filter: 'drop-shadow(0 4px 8px rgba(255, 215, 0, 0.3))',
              }}
            />
          ) : (
            <SvgCheckCircle1
              style={{
                width: isNarrowWidth ? '60px' : '80px',
                height: isNarrowWidth ? '60px' : '80px',
                color: '#4CAF50',
                filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))',
              }}
            />
          )}
        </View>

        {/* Celebration Text */}
        <Text
          style={{
            fontSize: isNarrowWidth ? '20px' : '24px',
            fontWeight: 700,
            color: isAllCompleted ? '#FFD700' : '#4CAF50',
            marginBottom: '10px',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {isAllCompleted ? '🎉 Congratulations!' : '✨ Great Job!'}
        </Text>

        <Text
          style={{
            fontSize: isNarrowWidth ? '14px' : '16px',
            color: theme.pageText,
            lineHeight: 1.4,
            marginBottom: '20px',
          }}
        >
          {isAllCompleted 
            ? 'You\'ve completed all onboarding tasks! You\'re now ready to make the most of GoodSteward.'
            : taskTitle 
              ? `You've completed: ${taskTitle}`
              : 'Task completed successfully!'
          }
        </Text>

        {/* Progress Stars */}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '20px',
          }}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <SvgStarFull
              key={star}
              style={{
                width: '20px',
                height: '20px',
                color: '#FFD700',
                opacity: showAnimation ? 1 : 0,
                animation: showAnimation 
                  ? `starGlow 0.6s ease-in-out ${star * 0.1}s both` 
                  : 'none',
              }}
            />
          ))}
        </View>

        {/* Confetti Effect */}
        {showAnimation && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            {[...Array(20)].map((_, i) => (
              <View
                key={i}
                className="confetti-piece"
                style={{
                  position: 'absolute',
                  width: '8px',
                  height: '8px',
                  backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][i % 5],
                  borderRadius: '50%',
                  top: '10%',
                  left: `${Math.random() * 100}%`,
                  animation: `confetti 3s ease-out ${i * 0.1}s both`,
                }}
              />
            ))}
          </View>
        )}
      </View>

      <style>
        {`
          @keyframes bounce {
            0%, 20%, 53%, 80%, 100% {
              transform: translate3d(0, 0, 0);
            }
            40%, 43% {
              transform: translate3d(0, -15px, 0);
            }
            70% {
              transform: translate3d(0, -7px, 0);
            }
            90% {
              transform: translate3d(0, -2px, 0);
            }
          }

          @keyframes starGlow {
            0% {
              opacity: 0;
              transform: scale(0) rotate(0deg);
            }
            50% {
              opacity: 1;
              transform: scale(1.2) rotate(180deg);
            }
            100% {
              opacity: 1;
              transform: scale(1) rotate(360deg);
            }
          }

          @keyframes confetti {
            0% {
              transform: translateY(0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(720deg);
              opacity: 0;
            }
          }
          
          /* Optimize animations for better performance */
          .celebration-container {
            will-change: transform, opacity;
            transform: translateZ(0);
            backface-visibility: hidden;
          }
          
          .celebration-icon {
            will-change: transform;
            transform: translateZ(0);
          }
          
          .confetti-piece {
            will-change: transform, opacity;
            transform: translateZ(0);
          }
        `}
      </style>
    </View>
  );
}
